<template>
  <b-modal
    id="save-as-template-modal"
    centered
    hide-header
    hide-footer
    body-class="p-0"
    content-class="dg-modal-content"
  >
    <div class="dg-modal">
      <div class="dg-header">
        <div class="dg-header-icon">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z" />
            <path d="M20 3v4" />
            <path d="M22 5h-4" />
          </svg>
        </div>
        <div>
          <h5 class="dg-title">
            Save as Template
          </h5>
          <p class="dg-subtitle">
            Save as a reusable template.
          </p>
        </div>
        <button
          class="dg-close-btn"
          :disabled="isLoading"
          @click="closeModal"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          ><line
            x1="18"
            y1="6"
            x2="6"
            y2="18"
          /><line
            x1="6"
            y1="6"
            x2="18"
            y2="18"
          /></svg>
        </button>
      </div>

      <div class="dg-tabs">
        <button
          class="dg-tab"
          :class="{ 'dg-tab--active': activeTab === 0 }"
          @click="activeTab = 0"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            class="dg-tab-icon"
          ><path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z" /><path d="M20 3v4" /><path d="M22 5h-4" /><path d="M4 17v2" /><path d="M5 18H3" /></svg>
          Basic
        </button>
        <button
          class="dg-tab"
          :class="{ 'dg-tab--active': activeTab === 1 }"
          @click="activeTab = 1"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            class="dg-tab-icon"
          ><path d="M20 7h-9" /><path d="M14 17H5" /><circle
            cx="17"
            cy="17"
            r="3"
          /><circle
            cx="7"
            cy="7"
            r="3"
          /></svg>
          Advanced
        </button>
      </div>

      <b-overlay
        :show="isLoading"
        variant="white"
      >
        <div class="dg-body">
          <div v-if="activeTab === 0">
            <div class="dg-field">
              <div class="dg-field-label-row">
                <label class="dg-label">Name</label>
                <button
                  class="dg-link-btn"
                  :disabled="isLoading"
                  @click="pasteProjectName"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    class="lucide lucide-clipboard-paste h-3 w-3"
                  ><path d="M15 2H9a1 1 0 0 0-1 1v2c0 .6.4 1 1 1h6c.6 0 1-.4 1-1V3c0-.6-.4-1-1-1Z" /><path d="M8 4H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2M16 4h2a2 2 0 0 1 2 2v2M11 14h10" /><path d="m17 10 4 4-4 4" /></svg>
                  Paste Project Name
                </button>
              </div>
              <input
                v-model="templateName"
                class="dg-input"
                placeholder="Enter template name..."
              >
            </div>

            <div class="dg-field">
              <label class="dg-label">Type</label>
              <div class="dg-chips">
                <button
                  v-for="option in typeOptions"
                  :key="option.value"
                  class="dg-chip"
                  :class="{ 'dg-chip--active': selectedType === option.value }"
                  @click="selectedType = option.value"
                >
                  {{ option.label }}
                </button>
              </div>
            </div>

            <div class="dg-field">
              <label class="dg-label">Style</label>
              <div class="dg-chips">
                <button
                  v-for="option in styleOptions"
                  :key="option.value"
                  class="dg-chip"
                  :class="{ 'dg-chip--active': selectedStyle === option.value }"
                  @click="selectedStyle = option.value"
                >
                  {{ option.label }}
                </button>
              </div>
            </div>

            <div class="rounded-md border bg-muted/30 p-2 dg-gallery-toggle">
              <div class="flex items-start gap-2 dg-gallery-toggle-inner">
                <input
                  id="save-template-also-gallery"
                  v-model="isAlsoAddToDesignGallery"
                  type="checkbox"
                  class="dg-checkbox"
                >
                <label
                  for="save-template-also-gallery"
                  class="dg-gallery-toggle-label"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    class="lucide lucide-image-plus h-3.5 w-3.5 text-primary"
                  ><path d="M16 5h6" /><path d="M19 2v6" /><path d="M21 11.5V19a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h7.5" /><path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21" /><circle
                    cx="9"
                    cy="9"
                    r="2"
                  /></svg>
                  Also add to Design Gallery
                </label>
              </div>
            </div>
          </div>

          <div v-if="activeTab === 1">
            <div class="dg-field">
              <label class="dg-label">Customer</label>
              <select
                v-model="customerId"
                class="dg-input"
                :disabled="!customersList.length"
              >
                <option
                  v-for="customer in customersList"
                  :key="customer.id"
                  :value="customer.id"
                >
                  {{ customer.name }}
                </option>
              </select>
            </div>

            <div class="dg-field">
              <label class="dg-label">Tags</label>
              <small class="dg-hint">Type and Style (set on Basic) are also tags. Add more here.</small>

              <div class="dg-tag-chips">
                <span class="dg-tag-chip dg-tag-chip--base">
                  {{ selectedTypeLabel }}
                  <span class="dg-tag-chip-kind">type</span>
                </span>
                <span class="dg-tag-chip dg-tag-chip--base">
                  {{ selectedStyleLabel }}
                  <span class="dg-tag-chip-kind">style</span>
                </span>
                <span
                  v-for="tag in selectedExtraTags"
                  :key="tag"
                  class="dg-tag-chip"
                >
                  {{ tag }}
                  <button
                    type="button"
                    class="dg-tag-chip-remove"
                    @click="removeExtraTag(tag)"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    ><path d="M18 6 6 18" /><path d="m6 6 12 12" /></svg>
                  </button>
                </span>
              </div>

              <div class="dg-tag-input-row">
                <input
                  v-model="customTagInput"
                  class="dg-input dg-input--sm"
                  placeholder="Add a tag..."
                  @keyup.enter.prevent="addTagFromInput"
                >
                <button
                  class="dg-tag-add-btn"
                  type="button"
                  @click="addTagFromInput"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  ><path d="M5 12h14" /><path d="M12 5v14" /></svg>
                </button>
              </div>

              <div class="dg-tag-suggestions">
                <button
                  v-for="tag in availableTagOptions"
                  :key="`tag-opt-${tag}`"
                  type="button"
                  class="dg-tag-suggestion"
                  @click="addExtraTag(tag)"
                >
                  + {{ tag }}
                </button>
              </div>
            </div>

            <div class="dg-field">
              <label class="dg-section-label">EXPORT</label>
              <div class="dg-row">
                <div class="dg-field dg-field--third">
                  <label class="dg-label">File Type</label>
                  <select
                    v-model="fileType"
                    class="dg-input"
                  >
                    <option
                      v-for="option in fileTypeOptions"
                      :key="option.value"
                      :value="option.value"
                    >
                      {{ option.text }}
                    </option>
                  </select>
                </div>
                <div class="dg-field dg-field--third">
                  <label class="dg-label">DPI</label>
                  <input
                    v-model.number="dpi"
                    class="dg-input"
                    type="number"
                    min="1"
                  >
                </div>
                <div class="dg-field dg-field--third">
                  <label class="dg-label">Max Size (KB)</label>
                  <input
                    v-model.number="maxFileSize"
                    class="dg-input"
                    type="number"
                    min="1"
                  >
                </div>
              </div>
            </div>

            <div class="dg-field">
              <label class="dg-section-label">THUMBNAIL</label>
              <div class="dg-row dg-row--align-end">
                <div class="dg-field dg-field--third">
                  <label
                    class="dg-label dg-label--sm"
                    for="thumbnail-width"
                  >Max Width</label>
                  <input
                    id="thumbnail-width"
                    v-model.number="thumbnailWidth"
                    class="dg-input"
                    type="number"
                    min="1"
                    :max="maxThumbnailWidth"
                  >
                </div>
                <div class="dg-field dg-field--third">
                  <label
                    class="dg-label dg-label--sm"
                    for="thumbnail-height"
                  >Max Height</label>
                  <input
                    id="thumbnail-height"
                    v-model.number="thumbnailHeight"
                    class="dg-input"
                    type="number"
                    min="1"
                    :max="maxThumbnailHeight"
                  >
                </div>
                <div class="dg-field dg-field--third">
                  <button
                    class="dg-btn dg-btn--outline dg-btn--block dg-btn--field-size"
                    @click="generateTemplateThumbnail"
                  >
                    Generate new
                  </button>
                </div>
              </div>

              <div class="dg-upload-link-row">
                <button
                  type="button"
                  class="dg-upload-link"
                  @click="showImageTools({ upload: true })"
                >
                  or upload image...
                </button>
              </div>

              <b-form-file
                v-if="showUploadSection"
                v-model="image"
                accept="image/*"
                class="mt-3"
                @input="handleImageChange"
              />

              <PreviewableImage
                v-if="isImageToolsVisible && templateImagePreview"
                :src="templateImagePreview"
                alt="template-thumbnail"
                class="mt-3"
              />

              <div
                v-if="image"
                class="mt-3"
              >
                <button
                  class="dg-btn dg-btn--primary dg-btn--block"
                  :disabled="isLoading"
                  @click="updateThumbnailOnly"
                >
                  Update Template Thumbnail
                </button>
              </div>
            </div>

            <div class="dg-checkbox-row">
              <input
                id="save-update-existing"
                v-model="isUpdateExistingTemplate"
                type="checkbox"
                class="dg-checkbox"
              >
              <label
                for="save-update-existing"
                class="dg-checkbox-label"
              >Update existing template</label>
            </div>
          </div>
        </div>
      </b-overlay>

      <div class="dg-footer">
        <small
          v-if="template?.id"
          class="dg-gallery-id"
        >Template ID: {{ template.id }}</small>
        <div class="dg-footer-actions">
          <button
            class="dg-btn dg-btn--ghost"
            :disabled="isLoading"
            @click="closeModal"
          >
            Cancel
          </button>
          <button
            class="dg-btn dg-btn--primary"
            :disabled="isSaveDisabled || isLoading"
            @click="handleSave"
          >
            {{ saveButtonText }}
          </button>
        </div>
      </div>
    </div>
  </b-modal>
</template>

<script>
import { EVENTS, saveAsTemplateEventBroker } from '@frontend/group/modules/save-as-template/event-broker';
import axios from 'axios';
import { GeneratePreviewImage } from '@frontend/group/generate-preview-image';
import { DESIRED_HEIGHT, DESIRED_WIDTH } from '@backend/template/generate-template-thumbnail';
import PreviewableImage from '@frontend/components/shared/PreviewableImage.vue';
import designGalleryModalSharedMixin from './design-gallery-modal-shared.mixin';

const ALL_CUSTOMERS_OPTION = { id: 0, name: 'All Customers' };

export default {
  name: 'SaveAsTemplateModal',
  components: {
    PreviewableImage,
  },
  mixins: [designGalleryModalSharedMixin],

  props: {
    customers: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      template: null,
      activeTab: 0,
      templateName: '',
      customerId: 0,
      selectedType: '',
      selectedStyle: '',
      selectedExtraTags: [],
      customTagInput: '',
      tags: [],
      isAlsoAddToDesignGallery: false,
      isUpdateExistingTemplate: false,
      isTemplateInUse: false,
      maxFileSize: null,
      dpi: null,
      fileType: '',
      isLoading: false,
      defaultMaxFileSize: 50,
      defaultDpi: 300,
      fileTypeOptions: [
        { value: '', text: 'Select' },
        { value: 'jpg', text: 'JPG' },
        { value: 'png', text: 'PNG' },
        { value: 'pdf', text: 'PDF' },
        { value: 'pptx', text: 'PPTX' },
        { value: 'jpg_from_psd', text: 'JPG from PSD' },
        { value: 'png_from_psd', text: 'PNG from PSD' },
      ],
      typeOptions: [
        { label: 'Flipbooks', value: 'type_flipbooks' },
        { label: 'PDP', value: 'type_pdp' },
        { label: 'Sell Sheet', value: 'type_sell_sheet' },
      ],
      styleOptions: [
        { label: 'Bold', value: 'style_bold' },
        { label: 'Clean', value: 'style_clean' },
        { label: 'Minimal', value: 'style_minimal' },
      ],
      customersList: [],
      projectName: '',
      templateImagePreview: '',
      image: null,
      previewObjectUrl: null,
      showUploadSection: false,
      isImageToolsVisible: false,
      thumbnailWidth: DESIRED_WIDTH,
      thumbnailHeight: DESIRED_HEIGHT,
      maxThumbnailWidth: DESIRED_WIDTH,
      maxThumbnailHeight: DESIRED_HEIGHT,
    };
  },

  computed: {
    isSaveDisabled() {
      return !this.templateName || this.isLoading;
    },

    finalTags() {
      const tags = [
        this.selectedType,
        this.selectedStyle,
        ...this.selectedExtraTags,
      ];

      if (this.isAlsoAddToDesignGallery) {
        tags.unshift('creative_studio');
      }

      return Array.from(new Set(tags.filter(Boolean)));
    },
    
    saveButtonText() {
      return this.isLoading ? 'Saving...' : (this.isAlsoAddToDesignGallery ? 'Save & Add to Gallery' : 'Save')
    }
  },

  beforeMount() {
    this.customersList = [ALL_CUSTOMERS_OPTION, ...this.customers];
  },

  mounted() {
    this.registerEventListeners();
  },

  methods: {
    registerEventListeners() {
      saveAsTemplateEventBroker.on(EVENTS.INIT, this.initializeModal);
      saveAsTemplateEventBroker.on(EVENTS.CLOSE, this.closeModal);
    },

    initializeModal({ preview }) {
      if (!preview?.template) {
        return;
      }

      this.resetForm();
      this.template = preview.template;
      this.template.preview = preview;

      this.updateThumbnailLimits();
      this.templateImagePreview = this.getTemplateImageUrl();

      this.templateName = this.isUpdateExistingTemplate
        ? this.template.name
        : `${this.template.name}_copy`;

      this.customerId = this.findCustomerId(this.template.customer_id);
      this.projectName = preview.data.project_name;

      this.applyTagPresetFromTemplate(this.parseTags(this.template.tags));
      this.extractTemplateSettings(this.template);
      this.fetchTags();
      this.checkIfTemplateIsInUse();

      this.$bvModal.show('save-as-template-modal');
    },

    resetForm() {
      this.activeTab = 0;
      this.template = null;
      this.templateName = '';
      this.customerId = 0;
      this.selectedType = '';
      this.selectedStyle = '';
      this.selectedExtraTags = [];
      this.customTagInput = '';
      this.tags = [];
      this.isAlsoAddToDesignGallery = false;
      this.isUpdateExistingTemplate = false;
      this.isTemplateInUse = false;
      this.isLoading = false;
      this.showUploadSection = false;
      this.isImageToolsVisible = false;
      this.image = null;
      this.projectName = '';

      this.maxFileSize = this.defaultMaxFileSize;
      this.dpi = this.defaultDpi;
      this.fileType = '';

      this.thumbnailWidth = DESIRED_WIDTH;
      this.thumbnailHeight = DESIRED_HEIGHT;
      this.maxThumbnailWidth = DESIRED_WIDTH;
      this.maxThumbnailHeight = DESIRED_HEIGHT;

      this.clearPreviewObjectUrl();
      this.templateImagePreview = '';
    },

    findCustomerId(customerId) {
      const foundCustomer = this.customersList.find(customer => `${customer.id}` === `${customerId}`);
      return foundCustomer ? foundCustomer.id : ALL_CUSTOMERS_OPTION.id;
    },

    parseTags(tagsString) {
      return tagsString ? tagsString.split(',').map(tag => tag.trim()).filter(Boolean) : [];
    },

    applyTagPresetFromTemplate(tags) {
      const typeTag = tags.find(tag => this.typeOptions.some(option => option.value === tag));
      const styleTag = tags.find(tag => this.styleOptions.some(option => option.value === tag));

      if (typeTag) {
        this.selectedType = typeTag;
      }

      if (styleTag) {
        this.selectedStyle = styleTag;
      }

      this.isAlsoAddToDesignGallery = tags.includes('creative_studio');
      this.selectedExtraTags = tags.filter(tag => (
        tag !== this.selectedType
        && tag !== this.selectedStyle
        && tag !== 'creative_studio'
      ));
    },

    extractTemplateSettings(template) {
      if (!template?.fields) {
        return;
      }

      template.fields.forEach(field => {
        try {
          const fieldData = JSON.parse(field.options);
          if (!fieldData?.Option1) {
            return;
          }

          switch (field.type) {
            case 'Max File Size':
              this.maxFileSize = parseInt(fieldData.Option1, 10);
              break;
            case 'DPI':
              this.dpi = parseInt(fieldData.Option1, 10);
              break;
            case 'File Type': {
              const inputFileType = fieldData.Option1;
              const foundOption = this.fileTypeOptions.find(option => (
                option.value === inputFileType
                || option.text.toLowerCase() === `${inputFileType}`.toLowerCase()
              ));
              this.fileType = foundOption ? foundOption.value : inputFileType;
              break;
            }
            default:
              break;
          }
        } catch (error) {
          // Keep legacy behavior: ignore malformed saved field payloads.
        }
      });
    },

    closeModal() {
      this.$bvModal.hide('save-as-template-modal');
    },

    pasteProjectName() {
      if (!this.projectName) {
        return;
      }

      this.templateName = this.projectName;
    },

    async handleSave() {
      this.isLoading = true;

      if (this.isTemplateInUse && this.isUpdateExistingTemplate) {
        return this.showModificationWarning();
      }

      this.saveTemplate();
    },

    showModificationWarning() {
      Swal.fire({
        text: 'This template has already been used in other layouts, modifications can change the design',
        showCancelButton: true,
        confirmButtonText: 'Save',
        cancelButtonText: 'Cancel',
        reverseButtons: true,
        icon: 'info',
      }).then(({ value }) => {
        if (value) {
          this.saveTemplate();
        } else {
          this.resetForm();
        }
      });
    },

    saveTemplate() {
      const formData = new FormData();

      formData.append('templateName', this.templateName);
      formData.append('selectedCustomerId', this.customerId || '');
      formData.append('tags', this.finalTags.join(','));
      formData.append('isUpdateExistingTemplate', this.isUpdateExistingTemplate);
      formData.append('settings[max_file_size]', parseInt(this.maxFileSize, 10) || this.defaultMaxFileSize);
      formData.append('settings[dpi]', parseInt(this.dpi, 10) || this.defaultDpi);
      formData.append('settings[file_type]', this.fileType || '');

      if (this.image) {
        formData.append('image', this.image);
      }

      saveAsTemplateEventBroker.fire(EVENTS.SAVE, formData);
    },

    async checkIfTemplateIsInUse() {
      const { data } = await axios.post('/banner/check_used_template', { templateId: this.template.id });
      this.isTemplateInUse = data;
    },

    async fetchTags() {
      const { data } = await axios.get(`/projects/template/${this.template.id}/tag`);
      this.tags = data.map(tag => tag.name).filter(Boolean);
    },

    async addTag(name) {
      const normalizedTag = `${name}`.trim();
      if (!normalizedTag || !this.template?.id) {
        return null;
      }

      const { data } = await axios.post(`/projects/template/${this.template.id}/tag`, { name: normalizedTag });
      return data?.name || normalizedTag;
    },

    async addTagFromInput() {
      const normalizedTag = `${this.customTagInput}`.trim();
      if (!normalizedTag) {
        return;
      }

      const hasTagInList = this.tags.some(tag => `${tag}`.toLowerCase() === normalizedTag.toLowerCase());

      if (!hasTagInList) {
        try {
          const createdTag = await this.addTag(normalizedTag);
          this.addExtraTag(createdTag || normalizedTag);
          return;
        } catch (error) {
          // Fall back to local add to keep UX responsive.
        }
      }

      this.addExtraTag(normalizedTag);
    },

    setImagePreview(file) {
      this.image = file;
      this.previewObjectUrl = URL.createObjectURL(file);
      this.templateImagePreview = this.previewObjectUrl;
      this.isImageToolsVisible = true;
    },

    async generateTemplateThumbnail() {
      const canvas = this.template?.preview?.canvas || this.template?.canvas;
      if (!canvas) {
        return;
      }

      this.updateThumbnailLimits();

      const htmlCanvas = canvas.toCanvasElement ? canvas.toCanvasElement() : canvas;
      const width = Math.min(this.thumbnailWidth || DESIRED_WIDTH, this.maxThumbnailWidth);
      const height = Math.min(this.thumbnailHeight || DESIRED_HEIGHT, this.maxThumbnailHeight);

      this.clearPreviewObjectUrl();
      this.showImageTools();

      const thumbnail = new GeneratePreviewImage(htmlCanvas).resize(width, height);
      this.setImagePreview(await thumbnail.file('template-thumbnail'));
    },

    updateThumbnailOnly() {
      if (!this.image || !this.template?.id) {
        return;
      }

      this.isLoading = true;

      const formData = new FormData();
      formData.append('currentTemplateId', this.template.id);
      formData.append('image', this.image);

      saveAsTemplateEventBroker.fire(EVENTS.SAVE_THUMBNAIL, formData);
    },

  },
};
</script>

<style scoped lang="scss">
@import "@/../sass/frontend/modal/design-modal";

.dg-gallery-toggle-inner {
  display: flex;
  align-items: center;
  gap: 8px;
}

.dg-gallery-toggle {
  border: 1px solid #dbe1e8;
  border-radius: 8px;
  background: hsl(210 40% 96.1% / .6);
  padding: 12px;
}

.dg-gallery-toggle-label{
  margin: 0;
}
</style>

